import React, { useState, useEffect } from 'react';
import { FiLayers, FiCheck, FiX, FiRotateCcw, FiSave } from 'react-icons/fi';

const CompoundChoiceQuestion = ({ 
  question, 
  onAnswer, 
  currentAnswer = null,
  isSubmitted = false,
  timeSpent = 0 
}) => {
  // Validate required props
  if (!question) {
    return (
      <div className="compound-choice-question bg-gray-100 p-4 sm:p-6 lg:p-8 mb-6">
        <div className="text-center text-gray-600">
          <FiLayers className="text-4xl mx-auto mb-4" />
          <p>Error: No question data provided</p>
        </div>
      </div>
    );
  }

  const [sectionAnswers, setSectionAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const [error, setError] = useState(null);

  // Initialize section answers from current answer
  useEffect(() => {
    if (currentAnswer && currentAnswer.sectionAnswers) {
      setSectionAnswers(currentAnswer.sectionAnswers);
    } else {
      setSectionAnswers({});
    }
  }, [currentAnswer]);

  // Parse answerSections from question and ensure proper structure
  let answerSections = [];
  try {
    answerSections = (question.answerSections || []).map((section, index) => ({
      ...section,
      id: section.id || `section_${index}`,
      title: section.title || `Section ${index + 1}`,
      question: section.question || section.title || `Section ${index + 1}`,
      options: section.options || [],
      allowMultiple: section.allowMultiple !== undefined ? section.allowMultiple : true,
      marks: section.marks || 1,
      description: section.description || null
    }));

    // If no answerSections but we have options, create a single section
    if (answerSections.length === 0 && question.options && question.options.length > 0) {
      answerSections.push({
        id: 'main_section',
        title: 'Main Question',
        question: question.text || 'Select the correct answer(s)',
        options: question.options,
        allowMultiple: question.allowMultiple !== undefined ? question.allowMultiple : true,
        marks: question.marks || 1,
        description: null
      });
    }

    // Debug logging for troubleshooting
    if (answerSections.length === 0) {
      console.warn('CompoundChoiceQuestion: No answer sections found', {
        questionId: question.id,
        questionType: question.type,
        hasOptions: !!question.options,
        optionsCount: question.options?.length || 0,
        hasAnswerSections: !!question.answerSections,
        answerSectionsCount: question.answerSections?.length || 0,
        questionStructure: Object.keys(question)
      });
    }
  } catch (err) {
    console.error('Error parsing question data:', err);
    setError(err.message);
    return (
      <div className="compound-choice-question bg-gray-100 p-4 sm:p-6 lg:p-8 mb-6">
        <div className="text-center text-gray-600">
          <FiLayers className="text-4xl mx-auto mb-4" />
          <p>Error parsing question data</p>
          <p className="text-sm mt-2">{err.message}</p>
        </div>
      </div>
    );
  }

  const handleSectionAnswer = (sectionId, answer) => {
    if (isSubmitted) return;

    setSectionAnswers(prev => ({
      ...prev,
      [sectionId]: answer
    }));
  };

  const clearAllAnswers = () => {
    if (isSubmitted) return;
    setSectionAnswers({});
  };

  const validateSubmission = () => {
    const missingSections = answerSections.filter(section => !sectionAnswers[section.id]);
    
    if (missingSections.length > 0) {
      alert(`Please answer all sections before submitting:\n${missingSections.map(s => s.title).join('\n')}`);
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (isSubmitting || !validateSubmission()) return;

    setIsSubmitting(true);
    
    try {
      await onAnswer({
        questionId: question.id,
        sectionAnswers,
        type: 'COMPOUND_CHOICE',
        timeSpent
      });
      
      setShowFeedback(true);
    } catch (error) {
      console.error('Error submitting compound choice answer:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSectionStatus = (sectionId) => {
    if (!showFeedback || !currentAnswer) return null;
    
    const sectionAnswer = sectionAnswers[sectionId];
    if (!sectionAnswer) return 'unanswered';
    
    // For compound choice, we need to check if the selected options match the correct ones
    const section = answerSections.find(s => s.id === sectionId);
    if (!section) return null;
    
    const selectedOptions = sectionAnswer.selectedOptions || [];
    const correctOptions = section.options.filter(opt => opt.isCorrect).map(opt => opt.id);
    
    if (selectedOptions.length === correctOptions.length && 
        selectedOptions.every(id => correctOptions.includes(id))) {
      return 'correct';
    } else {
      return 'incorrect';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'correct': return 'border-gray-500 bg-gray-50';
      case 'incorrect': return 'border-gray-500 bg-gray-50';
      case 'unanswered': return 'border-gray-500 bg-gray-50';
      default: return 'border-gray-300 bg-white';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'correct': return <FiCheck className="text-gray-600 text-lg" />;
      case 'incorrect': return <FiX className="text-gray-600 text-lg" />;
      case 'unanswered': return <FiX className="text-gray-600 text-lg" />;
      default: return null;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'correct': return 'Correct';
      case 'incorrect': return 'Incorrect';
      case 'unanswered': return 'Unanswered';
      default: return '';
    }
  };

  const renderSection = (section) => {
    const sectionAnswer = sectionAnswers[section.id] || {};
    const selectedOptions = sectionAnswer.selectedOptions || [];
    
    // Determine if this section allows multiple selections or single selection
    const isMultipleChoice = section.allowMultiple !== false; // Default to multiple choice
    const inputType = isMultipleChoice ? 'checkbox' : 'radio';

    const handleOptionChange = (optionId) => {
      if (isSubmitted) return;

      let newSelectedOptions;
      if (isMultipleChoice) {
        // Multiple choice: toggle selection
        newSelectedOptions = selectedOptions.includes(optionId)
          ? selectedOptions.filter(id => id !== optionId)
          : [...selectedOptions, optionId];
      } else {
        // Single choice: replace selection
        newSelectedOptions = [optionId];
      }

      handleSectionAnswer(section.id, {
        selectedOptions: newSelectedOptions,
        sectionId: section.id,
        isMultipleChoice
      });
    };

    // Handle different option types flexibly
    const renderOptions = () => {
      if (!section.options || section.options.length === 0) {
        return (
          <div className="text-gray-500 text-sm italic p-3 border border-dashed border-gray-300 rounded-lg">
            No options available for this section
          </div>
        );
      }

      return (
        <div className="space-y-3">
          {section.options.map((option, optionIndex) => (
            <label
              key={option.id || `option_${optionIndex}`}
              className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                                  selectedOptions.includes(option.id)
                    ? 'border-gray-500 bg-gray-50'
                  : 'border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50'
              }`}
            >
                          <input
              type={inputType}
              name={isMultipleChoice ? undefined : `section_${section.id}`}
              checked={selectedOptions.includes(option.id)}
              onChange={() => handleOptionChange(option.id)}
              disabled={isSubmitted}
              className={`w-4 h-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500 ${
                inputType === 'radio' ? 'focus:ring-gray-500' : 'focus:ring-gray-500'
              }`}
            />
              <div className="flex-1 min-w-0">
                <span className="text-gray-800 font-medium break-words">{option.text}</span>
              </div>
            </label>
          ))}
        </div>
      );
    };

    return renderOptions();
  };

  if (!answerSections || answerSections.length === 0) {
    return (
      <div className="compound-choice-question bg-gray-100 p-4 sm:p-6 lg:p-8 mb-6">
        <div className="text-center text-gray-500">
          <FiLayers className="text-4xl mx-auto mb-4" />
          <p>No answer sections available for this compound choice question.</p>
          {question.text && (
            <div className="mt-4 text-left bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-700 mb-2">Question:</h4>
              <p className="text-gray-600">{question.text}</p>
            </div>
          )}
          {question.options && question.options.length > 0 && (
            <div className="mt-4 text-left bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-700 mb-2">Available Options:</h4>
              <ul className="text-gray-600 space-y-1">
                {question.options.map((option, index) => (
                  <li key={option.id || index} className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                    {option.text}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="compound-choice-question bg-gray-100 p-4 sm:p-6 lg:p-8 mb-6">
      {/* Question Text */}
      <div className="mb-6">
        <div className="text-gray-900 text-base font-bold leading-relaxed">
          {question.text}
        </div>
      </div>

      {/* Images if any */}
      {question.images && question.images.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Supporting Materials:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {question.images.map((image, index) => (
              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                <img 
                  src={image.imageUrl} 
                  alt={image.altText || `Question image ${index + 1}`}
                  className="w-full h-auto max-h-64 object-contain"
                />
                {image.altText && (
                  <div className="p-3 bg-gray-50 text-sm text-gray-600">
                    {image.altText}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Answer Sections */}
      <div className="mb-6">
        <h4 className="text-lg font-semibold text-gray-800 mb-4">
          Answer Sections ({answerSections.length})
        </h4>
        
        <div className="space-y-6">
          {answerSections.map((section, index) => {
            const status = getSectionStatus(section.id);
            const sectionAnswer = sectionAnswers[section.id];
            
            return (
              <div 
                key={section.id} 
                className={`p-6 border-2 rounded-lg transition-all duration-200 ${
                  getStatusColor(status)
                }`}
              >
                {/* Section Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm font-medium">
                      Section {index + 1}
                    </span>
                    <h5 className="text-lg font-semibold text-gray-800">{section.title}</h5>
                    <span className={`text-xs px-2 py-1 rounded font-medium ${
                      section.allowMultiple !== false 
                        ? 'bg-gray-100 text-gray-700' 
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {section.allowMultiple !== false ? 'Multiple Choice' : 'Single Choice'}
                    </span>
                  </div>
                  
                  {/* Status Display */}
                  {showFeedback && status && (
                    <div className="flex items-center gap-2">
                                          <span className={`text-xs px-2 py-1 rounded font-medium ${
                      status === 'correct' 
                        ? 'bg-gray-100 text-gray-700' 
                        : status === 'incorrect'
                        ? 'bg-gray-100 text-gray-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {getStatusText(status)}
                    </span>
                      {getStatusIcon(status)}
                    </div>
                  )}
                </div>

                {/* Section Question */}
                <div className="text-gray-700 mb-4">
                  {section.question && (
                    <div className="mb-3">
                      <span className="text-sm font-medium text-gray-600">Question:</span>
                      <div className="text-gray-800 mt-1">{section.question}</div>
                    </div>
                  )}
                  
                  {/* Section Description/Instructions */}
                  {section.description && (
                    <div className="mb-3">
                      <span className="text-sm font-medium text-gray-600">Description:</span>
                      <div className="text-gray-700 mt-1 text-sm">{section.description}</div>
                    </div>
                  )}
                  
                  {/* Section Marks */}
                  {section.marks && (
                    <div className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs font-medium">
                      {section.marks} mark(s)
                    </div>
                  )}
                </div>

                {/* Section Options */}
                {renderSection(section)}

                {/* Section Answer Display */}
                {sectionAnswer && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                    <div className="text-sm text-gray-600">
                      <strong>Your answer:</strong> {sectionAnswer.selectedOptions?.length || 0} option(s) selected
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="mb-6">
        <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
          <span>Progress: {Object.keys(sectionAnswers).length} / {answerSections.length} sections completed</span>
          <span>{Math.round((Object.keys(sectionAnswers).length / answerSections.length) * 100)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-gray-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(Object.keys(sectionAnswers).length / answerSections.length) * 100}%` }}
          ></div>
        </div>
      </div>

      {/* Action Buttons */}
      {!isSubmitted && (
        <div className="flex justify-center items-center">
          <button
            onClick={clearAllAnswers}
            disabled={isSubmitting}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <FiRotateCcw className="text-sm" />
            Clear All Answers
          </button>
        </div>
      )}

      {/* Feedback Display */}
      {showFeedback && currentAnswer && (
        <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h4 className="text-sm font-semibold text-gray-800 mb-3">Your Answers Summary:</h4>
          <div className="space-y-2">
            {answerSections.map((section, index) => {
              const status = getSectionStatus(section.id);
              const sectionAnswer = sectionAnswers[section.id];
              
              return (
                <div key={section.id} className="flex items-center justify-between text-sm">
                  <span className="font-medium text-gray-700">
                    Section {index + 1}: {section.title}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded ${
                      status === 'correct' 
                        ? 'bg-gray-100 text-gray-700' 
                        : status === 'incorrect'
                        ? 'bg-gray-100 text-gray-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {getStatusText(status)}
                    </span>
                    {getStatusIcon(status)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompoundChoiceQuestion; 